import { useState, useEffect, useCallback } from "react";
import { Search, FileText, CheckCircle2, XCircle, HelpCircle, ExternalLink, ZoomIn, ZoomOut, History, User } from "lucide-react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { timeAgo, clsx } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const FILTERS = [["pending", "Pending"], ["needs_info", "Needs info"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]];
const BADGE = { under_review: "text-gold bg-gold/15", approved: "text-pos bg-pos/15", rejected: "text-neg bg-neg/15", resubmit_required: "text-brandblue bg-brandblue/15", submitted: "text-fx-s3 bg-white/5" };
const LABEL = { under_review: "pending", resubmit_required: "needs info" };
const ACTIONS = {
  approve: { label: "Approve", cls: "bg-pos hover:bg-emerald-500", icon: CheckCircle2, hint: "Optional note for the user" },
  reject: { label: "Reject", cls: "bg-neg hover:bg-red-600", icon: XCircle, hint: "Reason for rejection (required, shown to the user)" },
  "request-resubmit": { label: "Request more info", cls: "bg-brandblue hover:bg-blue-500", icon: HelpCircle, hint: "What should the user provide? (required, shown to the user)" },
};
const fileUrl = (fid) => `${API}/kyc/file/${fid}?auth=${localStorage.getItem("fx_token")}`;
const Badge = ({ s }) => <span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono uppercase", BADGE[s] || "text-fx-s3 bg-white/5")}>{(LABEL[s] || s || "").replace(/_/g, " ")}</span>;

function DocViewer({ f }) {
  const [zoom, setZoom] = useState(1);
  const isPdf = f.content_type === "application/pdf";
  return (
    <div className="rounded-lg border border-white/10 bg-fx-base overflow-hidden" data-testid={`kyc-doc-${f.doc_type}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 text-xs">
        <span className="font-semibold uppercase tracking-wider">{f.doc_type.replace(/_/g, " ")}</span>
        <div className="flex items-center gap-1 text-fx-s3">
          {!isPdf && <>
            <button onClick={() => setZoom((z) => Math.max(1, z - 0.5))} data-testid={`kyc-zoom-out-${f.doc_type}`} className="p-1 hover:text-white"><ZoomOut className="h-3.5 w-3.5" /></button>
            <span className="font-mono w-8 text-center">{zoom}x</span>
            <button onClick={() => setZoom((z) => Math.min(4, z + 0.5))} data-testid={`kyc-zoom-in-${f.doc_type}`} className="p-1 hover:text-white"><ZoomIn className="h-3.5 w-3.5" /></button>
          </>}
          <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`kyc-doc-open-${f.doc_type}`} className="p-1 hover:text-white"><ExternalLink className="h-3.5 w-3.5" /></a>
        </div>
      </div>
      <div className="h-72 overflow-auto grid place-items-center bg-black/30">
        {isPdf
          ? <iframe title={f.doc_type} src={fileUrl(f.file_id)} className="w-full h-full" />
          : <img src={fileUrl(f.file_id)} alt={f.doc_type} style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }} className={clsx("max-w-full transition-transform", zoom === 1 && "max-h-full object-contain")} />}
      </div>
      <div className="px-3 py-1.5 text-[10px] text-fx-s3 font-mono truncate">{f.original_filename || "file"}{f.size ? ` · ${(f.size / 1024).toFixed(0)} KB` : ""}</div>
    </div>
  );
}

function Field({ label, value }) {
  return <div><div className="text-[10px] uppercase text-fx-s3">{label}</div><div className="text-sm">{value || "—"}</div></div>;
}

function ReviewPanel({ uid, canReview, onDone }) {
  const [d, setD] = useState(null);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/admin/kyc/${uid}`).then((r) => setD(r.data)), [uid]);
  useEffect(() => { setD(null); load(); }, [load]);
  const submit = async () => {
    if (action !== "approve" && reason.trim().length < 3) return toast.error("Please enter a reason");
    setBusy(true);
    try { await api.post(`/admin/kyc/${uid}/${action}`, { reason }); toast.success("Decision saved, user notified"); setAction(null); setReason(""); load(); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    setBusy(false);
  };
  if (!d) return <div className="p-8 text-fx-s3 text-sm">Loading submission…</div>;
  const A = action && ACTIONS[action];
  return (
    <div className="space-y-5" data-testid="kyc-review-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><User className="h-4 w-4 text-brandblue" /><h3 className="font-display text-xl font-bold">{d.full_name}</h3><Badge s={d.status} /></div>
          <div className="text-xs text-fx-s3 font-mono mt-1">{d.user?.email} · submitted {timeAgo(d.submitted_at)}</div>
        </div>
        {canReview && (
          <div className="flex gap-2 flex-wrap">
            {Object.entries(ACTIONS).map(([k, a]) => (
              <Button key={k} onClick={() => { setAction(k); setReason(""); }} data-testid={`kyc-action-${k}`} size="sm" className={clsx(a.cls, "gap-1")}><a.icon className="h-4 w-4" /> {a.label}</Button>
            ))}
          </div>
        )}
      </div>
      {d.rejection_reason && d.status !== "approved" && <div className="rounded-lg border border-white/10 bg-fx-base p-3 text-sm" data-testid="kyc-last-reason"><span className="text-fx-s3">Last note: </span>{d.rejection_reason}</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 rounded-lg bg-fx-base border border-white/5 p-4">
        <Field label="Full name" value={d.full_name} /><Field label="Date of birth" value={d.dob} /><Field label="Country" value={d.country} />
        <Field label="ID number" value={d.national_id} /><Field label="Phone" value={d.user?.phone} /><Field label="Account created" value={d.user?.created_at && new Date(d.user.created_at).toLocaleDateString()} />
        <div className="col-span-2 md:col-span-3"><Field label="Address" value={d.address} /></div>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-wider text-fx-s3 mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Documents ({d.files.length})</h4>
        <div className="grid md:grid-cols-2 gap-3">{d.files.map((f) => <DocViewer key={f.file_id} f={f} />)}</div>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-wider text-fx-s3 mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Decision history</h4>
        <div className="space-y-2" data-testid="kyc-history">
          {d.history.length === 0 && <p className="text-sm text-fx-s3">No history yet.</p>}
          {d.history.map((h, i) => (
            <div key={i} className="flex items-start gap-3 text-sm border-l-2 border-white/10 pl-3">
              <Badge s={h.status} />
              <div className="min-w-0"><div className="text-fx-s3 text-xs">{h.by} · {timeAgo(h.at)}</div>{h.reason && <div className="text-sm">{h.reason}</div>}</div>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="kyc-decision-dialog">
          {A && <>
            <DialogHeader><DialogTitle className="font-display">{A.label}: {d.full_name}</DialogTitle></DialogHeader>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={A.hint} data-testid="kyc-decision-reason" className="bg-fx-base border-white/10 min-h-[90px]" />
            <DialogFooter><Button onClick={submit} disabled={busy} data-testid="kyc-decision-confirm" className={A.cls}>Confirm {A.label.toLowerCase()}</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminKyc() {
  const { user } = useAuth();
  const canReview = ["admin", "super_admin"].includes(user?.role) || (user?.permissions || []).includes("kyc.review");
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [sel, setSel] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/kyc", { params: { status: filter === "all" ? undefined : filter, search: search || undefined } }).then((r) => setRows(r.data));
    api.get("/admin/kyc/stats").then((r) => setStats(r.data));
  }, [filter, search]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-4" data-testid="admin-kyc">
      <div className="rounded-xl bg-fx-s1 border border-white/5 flex flex-col max-h-[80vh]">
        <div className="p-3 space-y-3 border-b border-white/5">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`kyc-filter-${k}`} className={clsx("px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors", filter === k ? "bg-brandblue border-brandblue text-white" : "border-white/10 text-fx-s3 hover:text-white")}>
                {l} <span className="font-mono opacity-70" data-testid={`kyc-count-${k}`}>{stats[k] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="relative"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-fx-s3" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or ID" data-testid="kyc-search" className="pl-9 h-9 bg-fx-base border-white/10" /></div>
        </div>
        <div className="overflow-y-auto flex-1">
          {rows.length === 0 && <p className="p-6 text-center text-sm text-fx-s3" data-testid="kyc-empty">No submissions here.</p>}
          {rows.map((k) => (
            <button key={k.user_id} onClick={() => setSel(k.user_id)} data-testid={`admin-kyc-${k.user_id}`} className={clsx("w-full text-left px-4 py-3 border-b border-white/[0.04] hover:bg-white/5 transition-colors", sel === k.user_id && "bg-brandblue/10 border-l-2 border-l-brandblue")}>
              <div className="flex items-center justify-between gap-2"><span className="font-medium truncate">{k.full_name}</span><Badge s={k.status} /></div>
              <div className="text-xs text-fx-s3 font-mono truncate mt-0.5">{k.user?.email}</div>
              <div className="text-[10px] text-fx-s3 mt-0.5">{k.country} · {Object.keys(k.documents || {}).length} docs · {timeAgo(k.submitted_at)}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-xl bg-fx-s1 border border-white/5 p-5 min-h-[400px]">
        {sel ? <ReviewPanel uid={sel} canReview={canReview} onDone={load} /> : <div className="h-full grid place-items-center text-fx-s3 text-sm" data-testid="kyc-select-hint">Select a submission to review.</div>}
      </div>
    </div>
  );
}
